import {
  CustomFilter,
  EllipsisSpan,
  FieldTable,
  Wrapper,
  DatePicker,
  useBreadcrumbRoutes,
  Paging,
  usePaging,
  Select,
  Tag,
  Form,
} from '@maxtropy/components';
import { apiV2GgdOperatePagePost, apiV2GgdDeviceListPost } from '@maxtropy/device-customer-apis-v2';
import { V2GgdOperatePagePostRequest } from '@maxtropy/device-customer-apis-v2/dist/ytt/apis/v2/ggdOperatePage.d';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

interface DeviceOption {
  label: string;
  value: number;
}
type SearchParams = Omit<V2GgdOperatePagePostRequest, 'page' | 'size'>;
interface TableItem {
  deviceId: number | undefined;
  deviceName: string;
  status: string;
  operateDistributeStatus: number;
  distributeTime: string;
  distributeMan: string;
}
enum DeviceValue {
  PROCESSING = 0,
  SUCCESS = 1,
  FAILURE = 2,
  ALREADY = 3,
}
enum DeviceLabel {
  PROCESSING = '下发中',
  SUCCESS = '下发成功',
  FAILURE = '下发失败',
  ALREADY = '已下发',
}
const PowerLightOperationLog = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [deviceOptions, setDeviceOptions] = useState<DeviceOption[]>([]);
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [form] = Form.useForm();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const cacheStr = urlSearchParams.get('cacheParams');
  const cacheParams = cacheStr ? (JSON.parse(cacheStr) as SearchParams) : {};
  const [searchParams, setSearchParams] = useState<SearchParams>(cacheParams);
  const [loading, setLoading] = useState<boolean>(false);
  const columns = [
    {
      title: '控制设备',
      dataIndex: 'deviceName',
      width: 200,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '控制指令',
      dataIndex: 'status',
      width: 200,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '下发状态',
      dataIndex: 'operateDistributeStatus',
      width: 200,
      ellipsis: { showTitle: true },
      render: (v: number) => (
        <span className={`cell status-tag `}>
          {v === DeviceValue.PROCESSING ? (
            <Tag border="solid" color="processing">
              {DeviceLabel.PROCESSING}
            </Tag>
          ) : v === DeviceValue.SUCCESS ? (
            <Tag border="solid" color="success">
              {DeviceLabel.SUCCESS}
            </Tag>
          ) : v === DeviceValue.FAILURE ? (
            <Tag border="solid" color="error">
              {DeviceLabel.FAILURE}
            </Tag>
          ) : (
            <Tag border="solid" color="default">
              {DeviceLabel.ALREADY}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: '操作时间',
      dataIndex: 'distributeTime',
      width: 200,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '操作人',
      dataIndex: 'distributeMan',
      width: 180,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
  ];

  const [tableData, setTableData] = useState<TableItem[]>([]);

  useEffect(() => {
    if (cacheParams) {
      form.setFieldsValue({
        ...cacheParams,
      });
    }
  }, [cacheParams]);

  const onFinish = (values: {
    deviceId?: string | number;
    distributeStatus?: string | number;
    timeRange?: [Dayjs, Dayjs];
  }) => {
    console.log(values);
    const params: SearchParams = {
      deviceId: values.deviceId,
      distributeStatus: values.distributeStatus,
      startTime: values.timeRange?.[0].valueOf(),
      endTime: values.timeRange?.[1].valueOf(),
    };
    console.log(params);
    let cacheParams = encodeURIComponent(
      JSON.stringify({
        ...params,
      })
    );
    let newUrl = `${window.location.pathname}?cacheParams=${cacheParams}`;
    window.history.replaceState(null, document.title, newUrl);
    setSearchParams(params);
    setPageOffset(1);
  };

  useEffect(() => {
    setLoading(true);
    apiV2GgdOperatePagePost({
      ...searchParams,
      page: pageOffset,
      size: pageSize,
    }).then(res => {
      if (res) setTotalCount(res.total!);
      const dataSource = (res?.list || []).map(item => ({
        deviceId: item.id,
        deviceName: item.deviceName ?? '--',
        status: item.status === 1 ? '合闸' : item.status === 0 ? '分闸' : '--',
        operateDistributeStatus: item.operateDistributeStatus ?? 0,
        distributeTime: dayjs(item.distributeTime).format('YYYY-MM-DD HH:mm:ss') ?? '--',
        distributeMan: item.distributeMan ?? '--',
      }));
      setTableData(dataSource);
      setLoading(false);
    });
  }, [pageOffset, pageSize, searchParams, setTotalCount]);

  // 获取设备列表
  useEffect(() => {
    const getDeviceList = async () => {
      const res = await apiV2GgdDeviceListPost({});
      const list = res.list || [];
      const options: DeviceOption[] = list
        .filter(item => item.deviceId != null)
        .map(item => ({
          label: item.deviceName!,
          value: item.deviceId!,
        }));
      setDeviceOptions(options);
    };
    getDeviceList();
  }, []);

  const onReset = () => {
    setSearchParams({});
    setPageOffset(1);
    window.history.replaceState(null, document.title, window.location.pathname);
  };
  const filters = (
    <CustomFilter<SearchParams> form={form} onFinish={val => onFinish(val)} onReset={onReset}>
      <Form.Item name="deviceId" label="控制设备">
        <Select
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          placeholder="请选择"
          style={{ width: '100%' }}
          maxTagCount="responsive"
          allowClear
          options={deviceOptions}
          optionFilterProp="label"
          showSearch
        />
      </Form.Item>
      <Form.Item label="下发状态" name="distributeStatus">
        <Select
          allowClear
          placeholder="请选择"
          maxTagCount={2}
          showSearch
          optionFilterProp="label"
          options={[
            { value: DeviceValue.PROCESSING, label: DeviceLabel.PROCESSING },
            { value: DeviceValue.SUCCESS, label: DeviceLabel.SUCCESS },
            { value: DeviceValue.FAILURE, label: DeviceLabel.FAILURE },
            { value: DeviceValue.ALREADY, label: DeviceLabel.ALREADY },
          ]}
        />
      </Form.Item>
      <Form.Item name="timeRange" label="操作时间">
        <DatePicker.RangePicker style={{ width: '100%' }} />
      </Form.Item>
    </CustomFilter>
  );
  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '操作日志' }]} filters={filters}>
      <FieldTable
        adjustWidth={true}
        cacheKey={'template_List_table'}
        rowKey={'id'}
        sticky={{
          getContainer: () => {
            return document.querySelector('.central-menu-content') as HTMLElement;
          },
        }}
        scroll={{ y: 450 }}
        loading={loading}
        dataSource={tableData}
        columns={columns}
      />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};
export default PowerLightOperationLog;
